import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Spinner from '@/components/ui/Spinner';
import PhaseEta from '@/components/production/PhaseEta';
import StageTracker from '@/components/production/StageTracker';
import { useProductionActions } from '@/hooks/useProductionActions';
import { CANCELLED_SENTINEL, type ProductionState } from '@/hooks/useProduction';
import { localizeEngineError } from '@/lib/engineErrors';

/**
 * Plain-language summary of where the run stands. The old header showed the raw
 * status word ("error"), which told a non-technical producer nothing about what to
 * do next.
 */
function statusMessage(
  state: ProductionState,
  t: (key: string, options?: Record<string, unknown>) => string,
): { tone: 'ok' | 'bad' | 'busy'; text: string } {
  switch (state.status) {
    case 'running':
      return { tone: 'busy', text: t('status.running') };
    case 'complete':
      return { tone: 'ok', text: t('status.complete') };
    case 'error':
      // A user-initiated cancel isn't a failure — say so plainly.
      if (state.error === CANCELLED_SENTINEL) return { tone: 'busy', text: t('status.cancelled') };
      // Engine errors arrive in English; the handful of known terminal messages are
      // localised, anything else falls through verbatim so a real diagnostic is
      // never swallowed.
      return { tone: 'bad', text: localizeEngineError(t, state.error) || t('status.error') };
    case 'starting':
      return { tone: 'busy', text: t('status.starting') };
    default:
      return { tone: 'busy', text: t('status.loading') };
  }
}

export default function ProductionHeader({
  state,
  productionId,
  backPath,
  backLabel,
}: {
  state: ProductionState;
  productionId?: string;
  backPath: string;
  backLabel: string;
}) {
  const { t } = useTranslation('production');
  const { busy, cancel, restartShoot, resume, restart } = useProductionActions(productionId);
  const [confirmShoot, setConfirmShoot] = useState(false);

  const running = state.status === 'running';
  const stopped = state.status === 'error' || state.status === 'complete';
  const msg = statusMessage(state, t);

  return (
    <div className="flex-shrink-0 border-b border-border px-7 py-4">
      <Link
        to={backPath}
        className="mb-3.5 inline-flex items-center gap-1.5 text-[13px] text-faint transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {backLabel}
      </Link>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-5">
        <div className="flex max-w-xl items-start gap-2.5">
          {msg.tone === 'busy' && <Spinner className="mt-0.5" />}
          {msg.tone === 'ok' && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />}
          {msg.tone === 'bad' && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
          <div>
            <p className="m-0 text-[13px] font-medium leading-relaxed">{msg.text}</p>
            <PhaseEta phaseTiming={state.phaseTiming} running={running} compact />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {running && (
            <Button variant="destructive" size="sm" disabled={!!busy} onClick={cancel}>
              {busy === 'cancel' ? t('action.cancelling') : t('action.cancel')}
            </Button>
          )}
          {productionId && (
            <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => setConfirmShoot(true)}>
              {t('action.restartShoot')}
            </Button>
          )}
          {stopped && (
            <Button size="sm" disabled={!!busy} onClick={resume}>
              {busy === 'resume' ? t('action.resuming') : t('action.resume')}
            </Button>
          )}
          {stopped && (
            <Button variant="ghost" size="sm" disabled={!!busy} onClick={restart}>
              {t('action.restart')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <StageTracker stages={state.stages} live={running} />
        {productionId && (
          <span className="rounded-md border border-border bg-elevated px-3 py-1.5 font-mono text-[11px] text-muted-foreground">
            {productionId}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={confirmShoot}
        onOpenChange={setConfirmShoot}
        title={t('restartShoot.title')}
        description={t('restartShoot.description')}
        warning={
          <>
            <b>{t('restartShoot.keptLabel')}</b> {t('restartShoot.kept')}
            <br />
            <b>{t('restartShoot.wipedLabel')}</b> {t('restartShoot.wiped')}
          </>
        }
        destructive
        confirmLabel={t('restartShoot.confirm')}
        onConfirm={restartShoot}
      />
    </div>
  );
}
